import { Injectable } from '@jack-henry/frontend-utils/di';
import { Action, WindowService } from '@treasury/utils';
import { CoBrowseMessagePayload } from './co-browse.types';
import { defineSupportAgentCursor, SupportAgentCursor } from './support-agent-cursor.component';

const CO_BROWSE_CHANNEL_NAME = 'banno-agent-channel';

/**
 * Service to manage the user side of co-browsing functionality.
 *
 * Establishes a channel between the user's application and the co-browse video tab on the same domain.
 * This service receives updates from that tab, which itself gets updates about the cursor position
 * of the support agent via a websocket.
 */
@Injectable()
export class CoBrowseService {
    constructor(private window: WindowService) {
        defineSupportAgentCursor();
    }

    private readonly agentChannel = new BroadcastChannel(CO_BROWSE_CHANNEL_NAME);

    private readonly cursors: Map<string, SupportAgentCursor> = new Map();

    private cleanupTasks: Action[] = [];

    public init() {
        const listener = (e: MessageEvent<CoBrowseMessagePayload>) => this.listenForCursors(e);
        this.agentChannel.addEventListener('message', listener);
        this.cleanupTasks.push(() => this.agentChannel.removeEventListener('message', listener));
    }

    public stop() {
        this.cleanupTasks.forEach(performTask => performTask());
        this.cleanupTasks = [];
        this.cursors.forEach(cursor => cursor.remove());
        this.cursors.clear();
    }

    private listenForCursors(event: MessageEvent<CoBrowseMessagePayload>) {
        // listen for messages that indicate the screen share or video chat has ended,
        // and the cursor should be removed.
        const { action, id, agent } = event.data;
        const agentId = id || agent;
        if (!agentId) {
            throw new Error('CoBrowse message missing agent id');
        }

        switch (action) {
            case 'close':
                this.removeCursor(agentId);
                break;
            case 'cursor': {
                const { mousePosition, agentName } = event.data;
                const cursor = this.cursors.get(agentId) || this.createCursor(agentId, agentName);
                this.moveCursor(cursor, mousePosition);
                break;
            }
            case 'start':
                this.createCursor(agentId, event.data.agentName);
                break;
            default:
                return;
        }
    }

    private removeCursor(agentId: string) {
        const cursor = this.cursors.get(agentId);
        if (!cursor) {
            return;
        }

        cursor.remove();
        this.cursors.delete(agentId);
    }

    private moveCursor(cursor: SupportAgentCursor, mousePosition: DOMRect) {
        const { x, y } = scaleMousePosition(mousePosition, this.window.document);
        cursor.style.left = `${x}px`;
        cursor.style.top = `${y}px`;
    }

    private createCursor(agentId: string, agentName = 'Agent'): SupportAgentCursor {
        const cursor = this.window.document.createElement(
            SupportAgentCursor.tagName
        ) as SupportAgentCursor;
        cursor.setAttribute('id', agentId);
        cursor.agentName = agentName;
        this.window.document.body.appendChild(cursor);
        this.cursors.set(agentId, cursor);
        return cursor;
    }
}

function scaleMousePosition(agentPosition: DOMRect, document: Document) {
    const {
        clientWidth: width,
        clientHeight: height,
        scrollLeft,
        scrollTop,
    } = document.documentElement;
    const { x, y } = document.body.getBoundingClientRect();
    const relativeX = x + scrollLeft * 2 + (agentPosition.x / agentPosition.width) * width;
    const relativeY = y + scrollTop * 2 + (agentPosition.y / agentPosition.height) * height;

    return {
        x: relativeX,
        y: relativeY,
    };
}
